import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../store'
import { fetchCurrentUser } from '../slices/authSlice'
import { useGetUsageQuery } from '../services/projectDataApi'
import { creditGate } from '../lib/creditGate'
import LoadingPane from './LoadingPane'
import SubscriptionGate from './SubscriptionGate'

interface RequireCreditsProps {
  children: React.ReactNode
}

/**
 * RequireCredits — overlays the full-screen plan picker when the logged-in user has run out of
 * credits. This wraps the project-CREATION surface (the Dashboard / new-project form) only, so the
 * gate appears when the user goes to start new work — not when they're merely viewing existing
 * projects, insights, etc. (those load normally regardless of credits). The other creation entry
 * point, the "New Exploration" button, gates itself the same way (see ExplorationsTab).
 *
 * A user with no active subscription has no credit allowance, so this gate presents the plan picker
 * on first login — and again once credits run out — until they choose a plan (including Free). If
 * usage fails to load the app renders normally — a usage/billing outage must never lock anyone out.
 */
export default function RequireCredits({ children }: RequireCreditsProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { user, checked, loading } = useSelector((s: RootState) => s.auth)
  const usageQuery = useGetUsageQuery(undefined, { skip: !user })
  const data = usageQuery.data

  // The credit balance can only be fetched once the session is known, so the gate decision needs two
  // round trips. The thunk no-ops if another mount already started it.
  useEffect(() => {
    if (!checked) dispatch(fetchCurrentUser())
  }, [dispatch, checked])

  // Only the FIRST usage fetch waits (isLoading, not isFetching), so the periodic refetches behind an
  // already-open page never flash the loading pane.
  const gate = creditGate({ checked, loading, signedIn: !!user, usageLoading: usageQuery.isLoading, availableCredits: data?.available_credits })
  if (gate === 'loading') {
    return <LoadingPane label="Checking your plan…" data-testid="require-credits-loading" />
  }

  if (gate === 'gate') {
    return <SubscriptionGate />
  }

  return <>{children}</>
}
